import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import {
  Package, Search, Download, Layers,
  DollarSign, Store, ShoppingCart, PieChart as PieChartIcon, 
  Calendar, X, List, Clock, Eye, FileText, Activity
} from "lucide-react";
import Sidebar from "../components/Sidebar";

// Reporting & Export Libraries
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#eab308', '#ec4899'];

// --- ADVANCED PREMIUM MULTI-STORE PDF EXPORT (LIVE DATA) ---
const exportReportPDF = async (config) => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  
  const renderHeader = (pageTitle, yPos = 17) => {
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, 297, 25, 'F');
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text(`SHRUTI ERP: ${pageTitle.toUpperCase()}`, 14, yPos);
    
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleString()}`, 220, 15);
  };

  renderHeader(config.title);
  let startY = 35;

  if (config.chartId) {
    const chartEl = document.getElementById(config.chartId);
    if (chartEl) {
      try {
        const canvas = await html2canvas(chartEl, { scale: 2, backgroundColor: '#ffffff' });
        const imgData = canvas.toDataURL('image/png');
        doc.addImage(imgData, 'PNG', 160, 30, 110, 75); 
      } catch (err) {
        console.error("Failed to capture chart for PDF", err);
      }
    }
  }

  if (config.kpis && config.kpis.length > 0) {
    autoTable(doc, {
      head: [['Summary Metric', 'Calculated Value']],
      body: config.kpis.map(b => [b.label, b.value]),
      startY: startY,
      tableWidth: 130,
      margin: { left: 14 },
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 5, textColor: [15, 23, 42] },
      headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [250, 250, 250] }
    });
    startY = Math.max(doc.lastAutoTable.finalY + 15, 115);
  } else {
    startY = 40;
  }

  const commonStyles = {
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3, textColor: [51, 65, 85] },
    headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    footStyles: { fillColor: [226, 232, 240], textColor: [15, 23, 42], fontStyle: 'bold' },
    margin: { bottom: 15 }
  };

  if (config.storeWiseData) {
    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text("Store-wise Summary", 14, startY);
    
    autoTable(doc, {
      ...commonStyles,
      startY: startY + 4,
      head: [['S.No', 'Store Name', 'Sold (Qty)', 'Sold Amount (Retail)', 'Closing Stock', 'Asset Value (Retail)']],
      body: config.storeWiseData.length > 0 ? config.storeWiseData : [['-', '-', '-', '-', '-', '-']],
      foot: [['', 'Global Total', config.totals.soldQty, `Rs. ${config.totals.soldAmt.toLocaleString()}`, config.totals.stockQty, `Rs. ${config.totals.stockAmt.toLocaleString()}`]],
    });
    startY = doc.lastAutoTable.finalY + 15;
  }

  if (config.productTypeData) {
    if (startY > 160) { doc.addPage(); renderHeader(`${config.title} - Continued`); startY = 35; }
    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text("Product Type Summary", 14, startY);

    autoTable(doc, {
      ...commonStyles,
      startY: startY + 4,
      head: [['S.No', 'Product Type', 'Total Quantity', 'Total Amount (Retail)']],
      body: config.productTypeData.length > 0 ? config.productTypeData : [['-', '-', '-', '-']],
      foot: [['', 'Overall Total', config.totals.stockQty, `Rs. ${config.totals.stockAmt.toLocaleString()}`]],
    });
    startY = doc.lastAutoTable.finalY + 15;
  }

  if (config.inventoryData) {
    if (startY > 160) { doc.addPage(); renderHeader(`${config.title} - Continued`); startY = 35; }
    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text("Inventory Ledger", 14, startY);

    autoTable(doc, {
      ...commonStyles,
      startY: startY + 4,
      head: [['S.No', 'SKU Code', 'Item Details', 'Sold (Qty)', 'Sold Amount', 'Stock (Qty)', 'Total Value']],
      body: config.inventoryData.length > 0 ? config.inventoryData : [['-', '-', 'No inventory data found', '-', '-', '-', '-']],
      foot: [['', 'Total', '-', config.totals.soldQty, `Rs. ${config.totals.soldAmt.toLocaleString()}`, config.totals.stockQty, `Rs. ${config.totals.stockAmt.toLocaleString()}`]],
    });
  }

  doc.save(`${config.title.replace(/\s+/g, '_')}_Report.pdf`);
};

const exportReportExcel = (config) => {
  const workbook = XLSX.utils.book_new();
  const summaryData = [
    [`SHRUTI ERP - ${config.title.toUpperCase()}`],
    [`Generated on: ${new Date().toLocaleString()}`],
    [],
    ["Metric", "Value"],
    ...config.kpis.map(s => [s.label, s.value])
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Report Summary");

  if (config.storeWiseData) {
    const storeSheetData = [
      ['S.No', 'Store Name', 'Sold (Qty)', 'Sold Amount (Retail)', 'Closing Stock', 'Asset Value (Retail)'],
      ...config.storeWiseData,
      ['', 'Global Total', config.totals.soldQty, config.totals.soldAmt, config.totals.stockQty, config.totals.stockAmt]
    ];
    const storeSheet = XLSX.utils.aoa_to_sheet(storeSheetData);
    XLSX.utils.book_append_sheet(workbook, storeSheet, "Store-wise Summary");
  }

  if (config.productTypeData) {
    const typeSheetData = [
      ['S.No', 'Product Type', 'Total Quantity', 'Total Amount (Retail)'],
      ...config.productTypeData,
      ['', 'Overall Total', config.totals.stockQty, config.totals.stockAmt]
    ];
    const typeSheet = XLSX.utils.aoa_to_sheet(typeSheetData);
    XLSX.utils.book_append_sheet(workbook, typeSheet, "Product Types");
  }

  if (config.inventoryData) {
    const invSheetData = [
      ['S.No', 'SKU Code', 'Item Details', 'Sold (Qty)', 'Sold Amount', 'Stock (Qty)', 'Total Value'],
      ...config.inventoryData,
      ['', 'Total', '-', config.totals.soldQty, config.totals.soldAmt, config.totals.stockQty, config.totals.stockAmt]
    ];
    const invSheet = XLSX.utils.aoa_to_sheet(invSheetData);
    XLSX.utils.book_append_sheet(workbook, invSheet, "Inventory Ledger");
  }
  XLSX.writeFile(workbook, `${config.title.replace(/\s+/g, '_')}_Report.xlsx`);
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 text-white p-3 rounded-lg shadow-xl border border-slate-700 text-xs font-bold">
        <p className="mb-1 text-slate-300">{payload[0].name}</p>
        <p className="text-emerald-400 text-sm">Stock: {payload[0].value.toLocaleString()} Units</p>
      </div>
    );
  }
  return null;
};

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function ClosingStock() {
  const [activeTab, setActiveTab] = useState("combined-summary");
  const [isLoading, setIsLoading] = useState(true);
  
  // Raw Data
  const [rawStoresData, setRawStoresData] = useState([]);
  const [syncedReports, setSyncedReports] = useState([]);
  
  // Filters
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // PDF Preview State
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const storesSnapshot = await getDocs(collection(db, "stores"));
        const storesList = storesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const fullData = [];
        const fullSyncedReports = [];

        for (const store of storesList) {
          // 1. Fetch live inventory and sales
          const inventorySnap = await getDocs(collection(db, "stores", store.id, "inventory"));
          const salesSnap = await getDocs(collection(db, "stores", store.id, "sales"));
          
          fullData.push({
            storeId: store.id,
            storeName: store.storeId || store.name || `Store ${store.id}`,
            inventory: inventorySnap.docs.map(d => ({ id: d.id, ...d.data() })),
            sales: salesSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          });

          // 2. Fetch Synced Closing Stock Reports generated by the store
          const reportsSnap = await getDocs(collection(db, "stores", store.id, "closing-stock"));
          reportsSnap.forEach(reportDoc => {
            fullSyncedReports.push({
              id: reportDoc.id,
              storeId: store.id, // Store/Branch ID
              storeName: store.storeId || store.name || `Store ${store.id}`,
              ...reportDoc.data()
            });
          });
        }
        
        setRawStoresData(fullData);
        setSyncedReports(fullSyncedReports);
      } catch (error) {
        console.error("Error fetching closing stock data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllData();
  }, []);

  // --- FILTERED LIVE DATA AGGREGATION ---
  const storesData = useMemo(() => {
    if (!rawStoresData.length) return [];
    return rawStoresData.map(store => {
      const salesMap = {};
      store.sales.forEach(sale => {
        if (sale.timestamp) {
          const saleDate = sale.timestamp.split("T")[0];
          if (startDate && saleDate < startDate) return;
          if (endDate && saleDate > endDate) return;
        }
        if (sale.items && Array.isArray(sale.items)) {
          sale.items.forEach(item => {
            const code = item.productCode?.toLowerCase();
            if (code) salesMap[code] = (salesMap[code] || 0) + (Number(item.quantity) || 0);
          });
        }
      });
      const processedInventory = store.inventory.map(item => ({
        ...item,
        soldQuantity: salesMap[item.productCode?.toLowerCase()] || 0
      }));
      return { ...store, inventory: processedInventory };
    });
  }, [rawStoresData, startDate, endDate]);

  // --- FILTERED SYNCED REPORTS (Daily default) ---
  const filteredSyncedReports = useMemo(() => {
    let filtered = syncedReports;
    if (startDate || endDate) {
      if (startDate) filtered = filtered.filter(r => r.date >= startDate);
      if (endDate) filtered = filtered.filter(r => r.date <= endDate);
    } else {
      const todayStr = new Intl.DateTimeFormat('en-CA', { 
        timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' 
      }).format(new Date());
      filtered = filtered.filter(r => r.date === todayStr);
    }
    return filtered.sort((a, b) => {
      if (a.date !== b.date) return b.date.localeCompare(a.date);
      return a.storeName.localeCompare(b.storeName);
    });
  }, [syncedReports, startDate, endDate]);

  // --- GLOBAL AGGREGATIONS (For live data) ---
  const globalSummary = useMemo(() => {
    let totalStock = 0, totalSold = 0, totalValue = 0, totalSoldValue = 0;
    let uniqueSKUs = new Set();
    const storeDistribution = [];
    const globalCategoryMap = {}; 

    storesData.forEach(store => {
      let storeStock = 0;
      store.inventory.forEach(item => {
        const qty = Number(item.quantity || 0);
        const sold = Number(item.soldQuantity || 0);
        const price = Number(item.retailPrice || 0); 
        const type = item.productType || 'Uncategorized';
        
        totalStock += qty; totalSold += sold; totalValue += (qty * price); totalSoldValue += (sold * price);
        storeStock += qty;
        
        if (item.productCode) uniqueSKUs.add(item.productCode);
        if (!globalCategoryMap[type]) globalCategoryMap[type] = { quantity: 0, amount: 0 };
        globalCategoryMap[type].quantity += qty;
        globalCategoryMap[type].amount += (qty * price); 
      });
      if (storeStock > 0) storeDistribution.push({ name: store.storeName, value: storeStock });
    });

    const globalCategoryTableData = Object.entries(globalCategoryMap)
      .map(([type, data]) => ({ productType: type, ...data }))
      .sort((a, b) => b.amount - a.amount);

    return { totalStock, totalSold, totalValue, totalSoldValue, uniqueSKUCount: uniqueSKUs.size, storeDistribution, globalCategoryTableData };
  }, [storesData]);

  const globalSummaryBlocks = [
    { label: "Total Active Stores", value: storesData.length.toString() },
    { label: "Global Unique SKUs", value: globalSummary.uniqueSKUCount.toString() },
    { label: "Global Sold Stock (Filtered)", value: globalSummary.totalSold.toLocaleString() },
    { label: "Global Sold Amount (Retail)", value: `Rs. ${globalSummary.totalSoldValue.toLocaleString()}` },
    { label: "Global Closing Stock (Current)", value: globalSummary.totalStock.toLocaleString() },
    { label: "Global Asset Value (Retail)", value: `Rs. ${globalSummary.totalValue.toLocaleString()}` }
  ];

  // --- STORE-SPECIFIC AGGREGATIONS (For live data) ---
  const getStoreStats = (store) => {
    let stock = 0, sold = 0, val = 0, soldAmount = 0;
    let uniqueSKUs = new Set();
    const categoryDist = {};
    const storeCategoryMap = {}; 

    store.inventory.forEach(item => {
      const q = Number(item.quantity || 0);
      const s = Number(item.soldQuantity || 0);
      const p = Number(item.retailPrice || 0); 
      const type = item.productType || 'Uncategorized';
      
      stock += q; sold += s; val += (q * p); soldAmount += (s * p);
      if (item.productCode) uniqueSKUs.add(item.productCode);
      if (q > 0) categoryDist[type] = (categoryDist[type] || 0) + q;
      if (!storeCategoryMap[type]) storeCategoryMap[type] = { quantity: 0, amount: 0 };
      storeCategoryMap[type].quantity += q;
      storeCategoryMap[type].amount += (q * p); 
    });

    const categoryDistributionArray = Object.entries(categoryDist).map(([name, value]) => ({ name, value }));
    const storeCategoryTableData = Object.entries(storeCategoryMap)
      .map(([type, data]) => ({ productType: type, ...data }))
      .sort((a, b) => b.amount - a.amount);

    return { stock, sold, val, soldAmount, uniqueSKUCount: uniqueSKUs.size, categoryDistributionArray, storeCategoryTableData };
  };

  const handleGlobalExport = (format) => {
    const config = {
      title: "Master Closing Stock Report", chartId: "global-closing-stock-chart", kpis: globalSummaryBlocks,
      storeWiseData: storesData.map((store, idx) => {
        const st = getStoreStats(store); return [idx + 1, store.storeName, st.sold, st.soldAmount, st.stock, st.val];
      }),
      productTypeData: globalSummary.globalCategoryTableData.map((c, idx) => [idx + 1, c.productType, c.quantity, c.amount]),
      totals: { soldQty: globalSummary.totalSold, soldAmt: globalSummary.totalSoldValue, stockQty: globalSummary.totalStock, stockAmt: globalSummary.totalValue }
    };
    format === 'pdf' ? exportReportPDF(config) : exportReportExcel(config);
  };

  const handleStoreExport = (format, store, storeStats) => {
    const filteredInventory = store.inventory.filter(p => 
      !search.trim() || 
      String(p.productName || "").toLowerCase().includes(search.toLowerCase()) || 
      String(p.productCode || "").toLowerCase().includes(search.toLowerCase())
    );

    const filteredTotals = filteredInventory.reduce((acc, curr) => {
      const sq = Number(curr.soldQuantity) || 0;
      const q = Number(curr.quantity) || 0;
      const p = Number(curr.retailPrice) || 0;
      acc.soldQty += sq; acc.soldAmt += (sq * p); acc.stockQty += q; acc.stockAmt += (q * p); return acc;
    }, { soldQty: 0, soldAmt: 0, stockQty: 0, stockAmt: 0 });

    const storeSummaryBlocks = [
      { label: "Store Name", value: store.storeName }, { label: "Unique SKUs", value: storeStats.uniqueSKUCount.toString() },
      { label: "Total Sold (Filtered)", value: storeStats.sold.toLocaleString() }, { label: "Total Sold Amount", value: `Rs. ${storeStats.soldAmount.toLocaleString()}` },
      { label: "Closing Stock (Current)", value: storeStats.stock.toLocaleString() }, { label: "Total Asset Value (Retail)", value: `Rs. ${storeStats.val.toLocaleString()}` }
    ];

    const config = {
      title: `${store.storeName} Stock Report`, chartId: `store-chart-${store.storeId}`, kpis: storeSummaryBlocks,
      productTypeData: storeStats.storeCategoryTableData.map((c, idx) => [idx + 1, c.productType, c.quantity, c.amount]),
      inventoryData: filteredInventory.map((item, idx) => [
        idx + 1, item.productCode || '-', item.productName || item.details || '-',
        item.soldQuantity || 0, (item.soldQuantity || 0) * (item.retailPrice || 0),
        item.quantity || 0, (item.quantity || 0) * (item.retailPrice || 0)
      ]),
      totals: filteredTotals
    };
    format === 'pdf' ? exportReportPDF(config) : exportReportExcel(config);
  };

  // --- A5 PDF PREVIEW GENERATOR FOR SYNCED SNAPSHOTS ---
  const handlePreviewSnapshotPDF = (report) => {
    const docPdf = new jsPDF("l", "mm", "a5");
    const pageWidth = docPdf.internal.pageSize.getWidth();
    
    docPdf.setFillColor(30, 41, 59);
    docPdf.rect(0, 0, pageWidth, 20, "F");
    docPdf.setFont("helvetica", "bold");
    docPdf.setFontSize(14); 
    docPdf.setTextColor(255, 255, 255);
    docPdf.text(`SHRUTI ERP - SYNCED STOCK REPORT`, 10, 10);
    
    docPdf.setFontSize(8);
    docPdf.setFont("helvetica", "normal");
    docPdf.setTextColor(148, 163, 184);
    docPdf.text(`Branch: ${report.storeName.toUpperCase()} (ID: ${report.storeId})`, 10, 15);
    
    docPdf.setTextColor(16, 185, 129);
    docPdf.text(`Shift: ${report.shift.replace('_', ' ')}`, pageWidth - 10, 10, { align: "right" });
    docPdf.setTextColor(255, 255, 255);
    docPdf.text(`Recorded Date: ${report.date}`, pageWidth - 10, 15, { align: "right" });

    docPdf.setFillColor(37, 99, 235);
    docPdf.rect(0, 20, pageWidth, 1.5, "F");

    const kpiRows = [
      ["Sold Units", String(report.soldStock || 0)],
      ["Closing Stock", String((report.closingStockBalance || 0).toLocaleString())],
      ["Stock Valuation", `Rs. ${(report.stockValuation || 0).toLocaleString()}`]
    ];

    autoTable(docPdf, {
      startY: 25,
      head: [["Metric", "Value"]],
      body: kpiRows,
      theme: "grid",
      tableWidth: 80, 
      styles: { fontSize: 7, cellPadding: 2, textColor: [30, 41, 59] },
      headStyles: { fillColor: [16, 185, 129], fontStyle: "bold" },
      margin: { left: 10 }
    });

    let activeY = Math.max(docPdf.lastAutoTable.finalY + 8, 55);

    docPdf.setFont("helvetica", "bold");
    docPdf.setFontSize(10);
    docPdf.setTextColor(30, 41, 59);
    docPdf.text("TYPE SUMMARY", 10, activeY);
    
    const typeRowsPDF = (report.productTypeSummary || []).map((item, idx) => [
      idx + 1,
      item.type,
      `${(item.quantity || 0).toLocaleString()}`,
      `Rs. ${(item.valuation || 0).toLocaleString()}`
    ]);

    autoTable(docPdf, {
      startY: activeY + 3,
      head: [["S.No", "Type", "Qty", "Valuation"]],
      body: typeRowsPDF.length > 0 ? typeRowsPDF : [["-", "-", "-", "-"]],
      theme: "striped",
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 10, right: 10 }
    });

    let ledgerStartY = docPdf.lastAutoTable.finalY + 12;
    if (ledgerStartY > 135) { docPdf.addPage(); ledgerStartY = 15; }

    docPdf.setFillColor(30, 41, 59);
    docPdf.rect(0, ledgerStartY - 6, pageWidth, 10, "F");
    docPdf.setFont("helvetica", "bold");
    docPdf.setFontSize(10);
    docPdf.setTextColor(255, 255, 255);
    docPdf.text(`MASTER STOCK LEDGER (SNAPSHOT)`, 10, ledgerStartY + 1);

    const ledgerRows = (report.masterStockLedger || []).map((item, idx) => [
      idx + 1,
      item.productCode || "-",
      (item.productName || "Unnamed").substring(0, 22), 
      item.productType || "-",
      item.soldQuantity || 0,
      item.closingQuantity || 0,
      (item.unitPrice || 0).toLocaleString(),
      (item.totalValuation || 0).toLocaleString()
    ]);

    autoTable(docPdf, {
      startY: ledgerStartY + 5,
      head: [["S.No", "SKU", "Name", "Type", "Sold", "Cls.", "Price", "Total Val"]],
      body: ledgerRows.length > 0 ? ledgerRows : [["-", "-", "No data saved", "-", "-", "-", "-", "-"]],
      theme: "grid",
      styles: { fontSize: 6, cellPadding: 1.5 }, 
      headStyles: { fillColor: [30, 41, 59] },
      margin: { left: 5, right: 5 } 
    });

    // ONLY PREVIEW
    setPdfPreviewUrl(docPdf.output("bloburl"));
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans antialiased selection:bg-blue-200">
      <Sidebar />

      {/* PDF PREVIEW MODAL */}
      <AnimatePresence>
        {pdfPreviewUrl && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4 md:p-10"
          >
            <div className="bg-white w-full max-w-6xl h-full rounded-2xl shadow-2xl flex flex-col overflow-hidden">
              <div className="p-4 bg-slate-800 text-white flex items-center justify-between">
                <h3 className="font-bold flex items-center gap-2"><Eye size={18} /> Document Preview</h3>
                <button onClick={() => setPdfPreviewUrl(null)} className="p-1 hover:bg-slate-700 rounded-md transition-colors">
                  <X size={20} />
                </button>
              </div>
              <div className="flex-1 w-full bg-slate-100">
                <iframe src={pdfPreviewUrl} className="w-full h-full border-none" title="PDF Preview" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 p-4 lg:p-8 overflow-x-hidden space-y-6">
        
        {/* HEADER */}
        <div className="bg-white border border-slate-200 p-5 rounded-3xl shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-xl font-black uppercase text-slate-800 flex items-center gap-3">
              <Layers className="text-emerald-600" size={24} /> 
              Master Inventory & Sales Engine
            </h1>
            <p className="text-slate-500 text-xs font-bold mt-1 tracking-wider">Comprehensive stock auditing for all branches</p>
          </div>
          
          {/* CALENDAR FILTER UI */}
          <div className="flex flex-wrap items-center gap-3">
             <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-600">
               <Calendar size={14} className="text-slate-400"/>
               <span>Start:</span>
               <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent outline-none font-mono cursor-pointer" />
             </div>
             <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-600">
               <Calendar size={14} className="text-slate-400"/>
               <span>End:</span>
               <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent outline-none font-mono cursor-pointer" />
             </div>
             {(startDate || endDate) && (
               <button onClick={() => { setStartDate(""); setEndDate(""); }} className="px-3 py-2 bg-rose-50 hover:bg-rose-100 hover:text-rose-600 text-rose-500 font-bold text-xs rounded-xl transition flex items-center gap-1">
                 <X size={14} /> Clear
               </button>
             )}
          </div>
        </div>

        {/* TOP NAVIGATION TABS */}
        <div className="flex flex-wrap gap-2 p-1.5 bg-white border border-slate-200 rounded-2xl shadow-sm">
          <button onClick={() => {setActiveTab("combined-summary"); setSearch("");}} className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all font-bold text-xs ${activeTab === "combined-summary" ? "bg-emerald-600 text-white shadow-md" : "text-slate-600 hover:bg-slate-50"}`}>
            <PieChartIcon size={16} /> All Stores Combined
          </button>
          
          <button onClick={() => {setActiveTab("saved-reports"); setSearch("");}} className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all font-bold text-xs ${activeTab === "saved-reports" ? "bg-indigo-600 text-white shadow-md" : "text-slate-600 hover:bg-slate-50"}`}>
            <FileText size={16} /> Synced Shift Reports
          </button>

          <div className="w-px h-8 bg-slate-200 mx-2 self-center hidden sm:block"></div>
          
          {storesData.map(store => (
            <button key={store.storeId} onClick={() => {setActiveTab(store.storeId); setSearch("");}} className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all font-bold text-xs ${activeTab === store.storeId ? "bg-blue-600 text-white shadow-md" : "text-slate-600 hover:bg-slate-50"}`}>
              <Store size={16} /> {store.storeName}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="space-y-6">
              
              {/* =========================================
                  TAB: SYNCED SHIFT REPORTS (V-CARD GRID VIEW)
                  ========================================= */}
              {activeTab === "saved-reports" && (
                <div className="space-y-6">
                  <div className="flex items-center gap-3 px-2">
                    <Clock className="text-indigo-600" size={24} />
                    <div>
                      <h2 className="text-lg font-black uppercase text-slate-800">Historical Shift Snapshots</h2>
                      <p className="text-sm text-slate-500 font-bold mt-0.5">
                        {startDate || endDate ? "Filtered Date Range Snapshots" : "Default: Today's Shift Reports"}
                      </p>
                    </div>
                  </div>

                  {filteredSyncedReports.length === 0 ? (
                    <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center shadow-sm">
                      <p className="text-slate-400 font-bold text-sm uppercase tracking-wider">No synced reports found for the selected dates.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredSyncedReports.map((report) => (
                        <div key={report.id} className="bg-white border border-slate-200 rounded-3xl shadow-sm hover:shadow-lg transition-all flex flex-col overflow-hidden">
                          
                          {/* Card Header */}
                          <div className="bg-slate-50 p-5 border-b border-slate-100 flex justify-between items-start">
                            <div>
                              <h3 className="font-black text-slate-800 text-base uppercase flex items-center gap-2">
                                <Store className="text-blue-500" size={16}/> {report.storeName}
                              </h3>
                              <p className="text-xs font-bold text-slate-400 mt-1 uppercase tracking-wider">Branch ID: {report.storeId}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-xs font-black text-slate-600 font-mono">{report.date}</span>
                              <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-md border ${report.shift.includes('Morning') ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-indigo-50 text-indigo-600 border-indigo-200'}`}>
                                {report.shift.replace('_', ' ')}
                              </span>
                            </div>
                          </div>

                          {/* Card Body - Summary KPIs */}
                          <div className="p-5 grid grid-cols-3 gap-4 border-b border-slate-50">
                            <div className="flex flex-col">
                              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Sold Units</span>
                              <span className="font-mono font-black text-purple-600">{report.soldStock?.toLocaleString() || 0}</span>
                            </div>
                            <div className="flex flex-col border-l border-slate-100 pl-4">
                              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Closing Qty</span>
                              <span className="font-mono font-black text-blue-600">{report.closingStockBalance?.toLocaleString() || 0}</span>
                            </div>
                            <div className="flex flex-col border-l border-slate-100 pl-4">
                              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1">Asset Value</span>
                              <span className="font-mono font-black text-emerald-600">₹{report.stockValuation?.toLocaleString() || 0}</span>
                            </div>
                          </div>

                          {/* Card Body - Type Summary List */}
                          <div className="flex-1 p-5 pt-2 bg-slate-50/30">
                            <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                              <Activity size={12}/> Product Type Breakdown
                            </h4>
                            <div className="max-h-32 overflow-y-auto custom-scrollbar pr-2">
                              {report.productTypeSummary && report.productTypeSummary.length > 0 ? (
                                <ul className="space-y-2">
                                  {report.productTypeSummary.map((item, idx) => (
                                    <li key={idx} className="flex justify-between items-center text-xs border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                                      <span className="font-bold text-slate-700">{item.type}</span>
                                      <div className="text-right">
                                        <span className="font-mono font-bold text-slate-500 mr-3">{item.quantity} Qty</span>
                                        <span className="font-mono font-black text-emerald-600">₹{item.valuation?.toLocaleString()}</span>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-xs text-slate-400 font-bold italic">No product type data recorded.</p>
                              )}
                            </div>
                          </div>

                          {/* Card Footer - Action */}
                          <div className="p-4 bg-white border-t border-slate-100">
                            <button 
                              onClick={() => handlePreviewSnapshotPDF(report)}
                              className="w-full py-2.5 bg-indigo-50 hover:bg-indigo-600 text-indigo-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
                            >
                              <Eye size={14} /> Preview Full PDF Report
                            </button>
                          </div>

                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* =========================================
                  TAB: COMBINED SUMMARY (ALL STORES)
                  ========================================= */}
              {activeTab === "combined-summary" && (
                <>
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h2 className="text-lg font-black uppercase text-slate-800">Global Overview Dashboard</h2>
                    <div className="flex gap-2">
                      <button onClick={() => handleGlobalExport('pdf')} className="px-5 py-2.5 bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 shadow-sm"><Download size={14}/> PDF Report</button>
                      <button onClick={() => handleGlobalExport('excel')} className="px-5 py-2.5 bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 shadow-sm"><Download size={14}/> Excel Report</button>
                    </div>
                  </div>

                  {/* Summary KPI Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                      <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><ShoppingCart size={14}/> Sold Stock (Filtered)</div>
                      <div className="text-3xl font-black text-purple-600">{globalSummary.totalSold.toLocaleString()} <span className="text-sm font-bold text-slate-400">Units</span></div>
                    </div>
                    <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                      <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><Package size={14}/> Total Closing Stock</div>
                      <div className="text-3xl font-black text-blue-600">{globalSummary.totalStock.toLocaleString()} <span className="text-sm font-bold text-slate-400">Units</span></div>
                    </div>
                    <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                      <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><DollarSign size={14}/> Global Asset Value (Retail)</div>
                      <div className="text-3xl font-black text-emerald-600">₹{globalSummary.totalValue.toLocaleString()}</div>
                    </div>

                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    <div className="lg:col-span-2 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm h-[400px] flex flex-col">
                      <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider mb-2 text-center border-b border-slate-100 pb-3">Stock Distribution by Store</h3>
                      <div id="global-closing-stock-chart" className="flex-1 w-full bg-white p-2 mt-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={globalSummary.storeDistribution} cx="50%" cy="50%" innerRadius={70} outerRadius={100} fill="#8884d8" paddingAngle={5} dataKey="value">
                              {globalSummary.storeDistribution.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                            </Pie>
                            <RechartsTooltip content={<CustomTooltip />} />
                            <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 'bold' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="lg:col-span-3 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col">
                      <h3 className="text-xs font-black uppercase text-slate-800 mb-4 border-b border-slate-100 pb-3">Store-wise Summary</h3>
                      <div className="overflow-x-auto flex-1 custom-scrollbar">
                        <table className="w-full text-left border-collapse text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0 font-bold">
                            <tr className="border-b border-slate-200">
                              <th className="p-4">S.No</th>
                              <th className="p-4">Store Name</th>
                              <th className="p-4 text-center">Sold (Qty)</th>
                              <th className="p-4 text-right">Sold Amount (Retail)</th>
                              <th className="p-4 text-center">Closing Stock</th>
                              <th className="p-4 text-right">Asset Value (Retail)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {storesData.map((store, i) => {
                              const st = getStoreStats(store);
                              return (
                                <tr key={i} className="hover:bg-slate-50">
                                  <td className="p-4 font-bold text-slate-500">{i + 1}</td>
                                  <td className="p-4 font-bold text-slate-700 flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                                    {store.storeName}
                                  </td>
                                  <td className="p-4 font-mono font-bold text-purple-600 text-center">{st.sold.toLocaleString()}</td>
                                  <td className="p-4 font-mono font-black text-purple-600 text-right">₹{st.soldAmount.toLocaleString()}</td>
                                  <td className="p-4 font-mono font-bold text-blue-600 text-center">{st.stock.toLocaleString()}</td>
                                  <td className="p-4 font-mono font-black text-emerald-600 text-right">₹{st.val.toLocaleString()}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-slate-100 font-black text-slate-800 sticky bottom-0 z-10 shadow-[0_-2px_4px_rgba(0,0,0,0.02)]">
                            <tr className="border-t-2 border-slate-200 text-[11px]">
                              <td colSpan="2" className="p-4 text-right uppercase text-slate-500">Global Total:</td>
                              <td className="p-4 text-center text-purple-600">{globalSummary.totalSold.toLocaleString()}</td>
                              <td className="p-4 text-right text-purple-700">₹{globalSummary.totalSoldValue.toLocaleString()}</td>
                              <td className="p-4 text-center text-blue-600">{globalSummary.totalStock.toLocaleString()}</td>
                              <td className="p-4 text-right text-emerald-600">₹{globalSummary.totalValue.toLocaleString()}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col mt-6">
                    <h3 className="text-xs font-black uppercase text-slate-800 mb-4 border-b border-slate-100 pb-3 flex items-center gap-2">
                      <List className="text-indigo-500" size={16}/> Global Product Type Summary (Retail Value)
                    </h3>
                    <div className="overflow-x-auto flex-1 custom-scrollbar max-h-[350px]">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0 font-bold z-10">
                          <tr className="border-b border-slate-200">
                            <th className="p-4">S.No</th>
                            <th className="p-4">Product Type</th>
                            <th className="p-4 text-center">Total Quantity</th>
                            <th className="p-4 text-right">Total Amount (Retail)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 font-medium">
                          {globalSummary.globalCategoryTableData.length > 0 ? globalSummary.globalCategoryTableData.map((cat, i) => (
                            <tr key={i} className="hover:bg-slate-50">
                              <td className="p-4 font-bold text-slate-500">{i + 1}</td>
                              <td className="p-4 font-bold text-slate-700">{cat.productType}</td>
                              <td className="p-4 font-mono font-bold text-blue-600 text-center">{cat.quantity.toLocaleString()}</td>
                              <td className="p-4 font-mono font-black text-emerald-600 text-right">₹{cat.amount.toLocaleString()}</td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="4" className="p-8 text-center text-slate-400 font-bold">No product types found.</td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot className="bg-slate-100 font-black text-slate-800 sticky bottom-0 z-10 shadow-[0_-2px_4px_rgba(0,0,0,0.02)]">
                          <tr className="border-t-2 border-slate-200 text-[11px]">
                            <td colSpan="2" className="p-4 text-right uppercase text-slate-500">Overall Total:</td>
                            <td className="p-4 text-center text-blue-600">{globalSummary.totalStock.toLocaleString()}</td>
                            <td className="p-4 text-right text-emerald-600">₹{globalSummary.totalValue.toLocaleString()}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {/* =========================================
                  TAB: INDIVIDUAL STORE REPORTS
                  ========================================= */}
              {activeTab !== "combined-summary" && activeTab !== "saved-reports" && storesData.map(store => {
                if (store.storeId !== activeTab) return null;
                
                const storeStats = getStoreStats(store);
                const filteredInventory = store.inventory.filter(p => 
                  !search.trim() || 
                  String(p.productName || "").toLowerCase().includes(search.toLowerCase()) || 
                  String(p.productCode || "").toLowerCase().includes(search.toLowerCase())
                );

                const filteredTotals = filteredInventory.reduce((acc, curr) => {
                  const sq = Number(curr.soldQuantity) || 0;
                  const q = Number(curr.quantity) || 0;
                  const p = Number(curr.retailPrice) || 0;
                  acc.soldQty += sq; acc.soldAmt += (sq * p);
                  acc.stockQty += q; acc.stockAmt += (q * p);
                  return acc;
                }, { soldQty: 0, soldAmt: 0, stockQty: 0, stockAmt: 0 });

                return (
                  <div key={store.storeId} className="space-y-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <h2 className="text-lg font-black uppercase text-slate-800 flex items-center gap-2">
                        <Store className="text-blue-600" size={20}/> {store.storeName} Dashboard
                      </h2>
                      <div className="flex gap-2">
                        <button onClick={() => handleStoreExport('pdf', store, storeStats)} className="px-5 py-2.5 bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 shadow-sm"><Download size={14}/> Store PDF</button>
                        <button onClick={() => handleStoreExport('excel', store, storeStats)} className="px-5 py-2.5 bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 shadow-sm"><Download size={14}/> Store Excel</button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                        <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><ShoppingCart size={14}/> Store Sold Stock</div>
                        <div className="text-3xl font-black text-purple-600">{storeStats.sold.toLocaleString()} <span className="text-sm font-bold text-slate-400">Units</span></div>
                      </div>
                      <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                        <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><Package size={14}/> Store Closing Stock</div>
                        <div className="text-3xl font-black text-blue-600">{storeStats.stock.toLocaleString()} <span className="text-sm font-bold text-slate-400">Units</span></div>
                      </div>
                      <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                        <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><DollarSign size={14}/> Store Asset Value (Retail)</div>
                        <div className="text-3xl font-black text-emerald-600">₹{storeStats.val.toLocaleString()}</div>
                      </div>
                      <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
                        <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2"><Layers size={14}/> Unique SKUs</div>
                        <div className="text-3xl font-black text-amber-500">{storeStats.uniqueSKUCount} <span className="text-sm font-bold text-slate-400">Items</span></div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="lg:col-span-1 space-y-6">
                        <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm h-[320px] flex flex-col">
                          <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider mb-2 text-center border-b border-slate-100 pb-3">Category Distribution</h3>
                          <div id={`store-chart-${store.storeId}`} className="flex-1 w-full bg-white p-2 mt-2">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie data={storeStats.categoryDistributionArray} cx="50%" cy="50%" innerRadius={50} outerRadius={80} fill="#8884d8" paddingAngle={5} dataKey="value">
                                  {storeStats.categoryDistributionArray.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                </Pie>
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 'bold' }} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                        
                        <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col">
                          <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider mb-4 border-b border-slate-100 pb-3 flex items-center gap-2">
                            <List className="text-indigo-500" size={16}/> Product Type Summary
                          </h3>
                          <div className="overflow-x-auto flex-1 custom-scrollbar max-h-[300px]">
                            <table className="w-full text-left border-collapse text-xs">
                              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0 font-bold text-[10px] z-10">
                                <tr className="border-b border-slate-200">
                                  <th className="p-3">S.No</th>
                                  <th className="p-3">Type</th>
                                  <th className="p-3 text-center">Qty</th>
                                  <th className="p-3 text-right">Amount (Retail)</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 font-medium">
                                {storeStats.storeCategoryTableData.length > 0 ? storeStats.storeCategoryTableData.map((cat, i) => (
                                  <tr key={i} className="hover:bg-slate-50 transition-colors">
                                    <td className="p-3 font-bold text-slate-500">{i + 1}</td>
                                    <td className="p-3 font-bold text-slate-700">{cat.productType}</td>
                                    <td className="p-3 font-mono font-bold text-blue-600 text-center">{cat.quantity.toLocaleString()}</td>
                                    <td className="p-3 font-mono font-black text-emerald-600 text-right">₹{cat.amount.toLocaleString()}</td>
                                  </tr>
                                )) : (
                                  <tr>
                                    <td colSpan="4" className="p-6 text-center text-slate-400 font-bold">No product types found.</td>
                                  </tr>
                                )}
                              </tbody>
                              <tfoot className="bg-slate-100 font-black text-slate-800 sticky bottom-0 z-10 shadow-[0_-2px_4px_rgba(0,0,0,0.02)]">
                                <tr className="border-t-2 border-slate-200 text-[11px]">
                                  <td colSpan="2" className="p-3 text-right uppercase text-slate-500">Total:</td>
                                  <td className="p-3 text-center text-blue-600">{storeStats.stock.toLocaleString()}</td>
                                  <td className="p-3 text-right text-emerald-600">₹{storeStats.val.toLocaleString()}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </div>

                      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl shadow-sm flex flex-col overflow-hidden">
                        <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row justify-between items-center gap-4">
                          <h3 className="text-sm font-black uppercase text-slate-800">Inventory Ledger</h3>
                          <div className="relative w-full sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SKU or Name..." className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-blue-500 transition-colors shadow-sm"/>
                          </div>
                        </div>
                        <div className="overflow-x-auto flex-1 custom-scrollbar" style={{ maxHeight: "calc(100% - 73px)" }}>
                          <table className="w-full text-left border-collapse text-xs relative">
                            <thead className="sticky top-0 bg-white shadow-sm z-10 font-bold text-slate-500 uppercase tracking-wider text-[10px]">
                              <tr className="border-b border-slate-200">
                                <th className="p-4 pl-6">S.No</th>
                                <th className="p-4">SKU Code</th>
                                <th className="p-4">Item Details</th>
                                <th className="p-4 text-center text-purple-600">Sold (Qty)</th>
                                <th className="p-4 text-right text-purple-600">Sold Amount</th>
                                <th className="p-4 text-center text-blue-600">Stock (Qty)</th>
                                <th className="p-4 text-right pr-6 text-emerald-600">Total Value</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-medium pb-10">
                              {filteredInventory.length > 0 ? filteredInventory.map((p, index) => (
                                <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                                  <td className="p-4 pl-6 font-bold text-slate-500">{index + 1}</td>
                                  <td className="p-4 font-mono text-slate-600 font-black">{p.productCode || '-'}</td>
                                  <td className="p-4 font-bold text-slate-800">{p.productName || p.details || 'N/A'}</td>
                                  <td className="p-4 font-mono font-black text-center text-purple-600">{p.soldQuantity || 0}</td>
                                  <td className="p-4 font-mono font-black text-right text-purple-600">₹{((p.soldQuantity || 0) * (p.retailPrice || 0)).toLocaleString()}</td>
                                  <td className="p-4 font-mono font-black text-center text-blue-600 text-sm">
                                    <span className={(p.quantity || 0) < 5 ? "text-rose-600 bg-rose-50 px-2 py-1 rounded-md" : ""}>{p.quantity || 0}</span>
                                  </td>
                                  <td className="p-4 font-mono text-emerald-600 font-black text-right pr-6">₹{((p.quantity || 0) * (p.retailPrice || 0)).toLocaleString()}</td>
                                </tr>
                              )) : (
                                <tr>
                                  <td colSpan="7" className="p-8 text-center text-slate-400 font-bold">No inventory records match your search.</td>
                                </tr>
                              )}
                            </tbody>
                            <tfoot className="bg-slate-100 font-black text-slate-800 sticky bottom-0 z-10 shadow-[0_-2px_4px_rgba(0,0,0,0.02)]">
                              <tr className="border-t-2 border-slate-200 text-[11px]">
                                <td colSpan="3" className="p-4 pl-6 text-right uppercase text-slate-500">View Total:</td>
                                <td className="p-4 text-center text-purple-700">{filteredTotals.soldQty.toLocaleString()}</td>
                                <td className="p-4 text-right text-purple-700">₹{filteredTotals.soldAmt.toLocaleString()}</td>
                                <td className="p-4 text-center text-blue-700">{filteredTotals.stockQty.toLocaleString()}</td>
                                <td className="p-4 text-right pr-6 text-emerald-700">₹{filteredTotals.stockAmt.toLocaleString()}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}